import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { PortableText } from '@portabletext/react';
// REMOVED: import { urlFor } from '@/lib/sanity';
import { fetchPostBySlug } from '@/lib/sanity';
import { ptComponents } from '@/lib/ptComponents';
import NavBar from '@/components/navigation/NavBar';
import Footer from '@/components/navigation/Footer';
import ContactForm from "@/components/form/ContactForm";
import DialogModal from '@/components/navigation/DialogDynamic';
import { FileIcon, ArrowLeftIcon, HomeIcon } from '@radix-ui/react-icons';
import { Skeleton } from '@/components/ui/skeleton'; // Assuming Skeleton component
import ButtonPrime from '@/components/navigation/ButtonPrime';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { trackRatgeberStart, trackRelatedRatgeberClick, trackRatgeberAbgeschlossen } from '@/lib/meta/pixel-tracker';
// --- Loading State Component (Keep PostSkeleton as before) ---
const PostSkeleton = () => ("")

// --- Error Component (Optional but recommended) ---
const ErrorComponent = ({ error, navigate }) => (
    <div className='bg-prime-50 min-h-screen'>
        <NavBar className="relative bg-white/100" />
        <div className="container mx-auto text-center py-20 px-4">
            <p className="text-red-600 text-xl mb-4">{error || 'Ein Fehler ist aufgetreten.'}</p>
            <button
                onClick={() => navigate("/ratgeber")}
                className="inline-flex items-center gap-2 px-4 py-2 bg-prime-600 text-white rounded hover:bg-prime-700"
            >
                <ArrowLeftIcon /> Zurück zum Ratgeber
            </button>
        </div>
        <Footer />
    </div>
);


export default function EstatePost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function getPost() {
      setLoading(true);
      setError(null);
      try {
        const fetchedPost = await fetchPostBySlug(slug);
        if (fetchedPost) {
          setPost(fetchedPost);
        } else {
          setError('Beitrag nicht gefunden.');
        }
      } catch (err) {
        setError('Fehler beim Laden des Beitrags.');
        console.error("Fetch error:", err); // Log the actual error
      } finally {
        setLoading(false);
      }
    }
    if (slug) {
      getPost();
    } else {
      setError('Kein Slug angegeben.');
      setLoading(false);
    }
  }, [slug]);


  // --- Render States ---
  if (loading) {
    return (
      <div className='bg-prime-50 min-h-screen'>
          <NavBar className="relative bg-white/100" />
          {/* Render a simpler header skeleton */}
          <Skeleton className="w-full h-[40vh] md:h-[60vh]" />
          <PostSkeleton /> {/* Content skeleton */}
          <Footer />
      </div>
    );
  }

  if (error) {
    return <ErrorComponent error={error} navigate={navigate} />;
  }

  if (!post) return null; // Should be caught by error state
  // --- Main Render ---
  return (
    <div className='bg-prime-50'>
      
        <NavBar className="relative bg-white/100" />
       

        {/* Header Section */}
        <div className="relative w-full h-[40vh] md:h-[60vh] bg-gray-200"> {/* Lighter placeholder */}
           {/* Check for direct mainImage URL */}
           {post.mainImage ? (
              <img
                src={post.mainImage.asset.url} // USE DIRECT URL
                alt={post.mainImage.alt || post.title}
                className="w-full h-full object-cover"
              />
           ) : (
             <div className="w-full h-full bg-gradient-to-r from-prime-100 to-prime-200 flex items-center justify-center">
                <p className="text-prime-700 text-lg">Kein Hauptbild</p>
             </div>
           )}
        </div>

        {/* Breadcrumbs */}


        {/* Main Content Card */}
        <article className="container mx-auto bg-white px-4 sm:px-6 md:px-10 py-8 md:pb-12 md:pt-3 rounded-lg shadow-lg mb-16 max-w-6xl relative z-10 -mt-10 md:-mt-16"> {/* Adjusted overlap */}
        <div className='flex items-center py-5'>
      <Breadcrumb className="py-2">
  <BreadcrumbList>
    <BreadcrumbItem>
      <BreadcrumbLink><RouterLink to="/" className="hover:text-prime-700  items-center gap-1"><HomeIcon className="w-4 h-4 inline" /> Startseite</RouterLink></BreadcrumbLink>
    </BreadcrumbItem>
    <BreadcrumbSeparator />
    <BreadcrumbItem>
      <BreadcrumbLink><RouterLink to="/ratgeber" className="hover:text-prime-700">Ratgeber</RouterLink></BreadcrumbLink>
    </BreadcrumbItem>
    <BreadcrumbSeparator />
    <BreadcrumbItem>
      <BreadcrumbPage>{post.title}</BreadcrumbPage>
    </BreadcrumbItem>
  </BreadcrumbList>
</Breadcrumb>
      </div>
          <header className="mb-8 md:mb-12 border-b pb-6">
             {/* Category Link - Check for category data */}
             {post.category?.name && (
                 <RouterLink
                   // Make sure category has a slug fetched if you link by slug
                   to={`/ratgeber${post.category.slug?.current ? `?category=${post.category.slug.current}` : ''}`}
                   className='inline-block mb-4 px-3 py-1 border border-prime-300 text-prime-600 bg-prime-50 hover:bg-prime-100 hover:border-prime-400 text-sm font-medium rounded-full transition-colors'
                 >
                   {post.category.name}
                 </RouterLink>
             )}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-prime-950 leading-tight mb-3">
                 {post.title}
              </h1>
             {post.subtitle && ( /* Check if subtitle exists */
                 <p className="text-lg md:text-xl text-gray-600 mt-2">
                     {post.subtitle}
                 </p>
             )}
              {/* Author and Date */}
             {(post.author || post.publishedAt) && ( // Check if author NAME or date exists
                <div className="flex items-center gap-3 mt-5 text-sm text-gray-500">
                    {/* Check for direct Author Image URL */}
                    {post.authorImage && (
                        <img
                            src={post.authorImage} // USE DIRECT URL
                            // Alt text should ideally come from author schema if possible
                            alt={post.author || 'Author'}
                            className="w-10 h-10 rounded-full object-cover"
                        />
                    )}
                   <div>
                       {post.author && ( // Check if author NAME exists
                            <span className="font-medium text-gray-700">Von {post.author}</span>
                       )}
                        {post.publishedAt && (
                             <span className="ml-1">
                                • Veröffentlicht am {new Date(post.publishedAt).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' })}
                             </span>
                        )}
                   </div>
                </div>
              )}
          </header>

          {/* Rich Text Content */}
          <div className="prose prose-lg lg:prose-xl max-w-none prose-headings:text-prime-900 prose-a:text-prime-600 hover:prose-a:text-prime-800 prose-strong:font-semibold">
            {/* Check if content exists before rendering */}
            {post.content ? (
                <PortableText
                    value={post.content}
                    components={ptComponents} // Use updated components expecting value.url
                />
            ) : (
                <p>Inhalt wird geladen oder ist nicht verfügbar.</p>
            )}
          </div>

          {/* Ratgeber PDF Section */}
          {/* Check for Ratgeber object AND its fileUrl */}
          {post.relatedRatgeber?.fileUrl && (
            <aside className="mt-12 md:mt-16 border-t border-prime-200 pt-8 bg-gradient-to-r from-prime-50 to-blue-50 py-6 px-4 rounded-lg shadow-sm">
              <h3 className="text-xl md:text-2xl font-semibold text-prime-900 mb-5">Passender Ratgeber zum Download</h3>
              <div className="flex flex-col max- sm:flex-row items-start sm:items-center gap-5">
                 {/* Check for direct Ratgeber Image URL */}
                 {post.relatedRatgeber.imageUrl && (
                     <img
                       src={post.relatedRatgeber.imageUrl} // USE DIRECT URL
                       alt={post.relatedRatgeber.title || 'Ratgeber Cover'}
                       className="max-h-[15rem] md:w-1/2 object-cover rounded-md "
                     />
                 )}
                <div className="flex-grow w-full">
                  <h4 className="text-lg font-semibold text-prime-800">{post.relatedRatgeber.title || 'Ratgeber'}</h4>
                  {post.relatedRatgeber.description && (
                      <p className="text-sm text-gray-700 mt-1 mb-3">{post.relatedRatgeber.description}</p>
                  )}

                  <DialogModal
                    trigger={<ButtonPrime onClick={() => trackRatgeberStart(post.relatedRatgeber.fileUrl, post.relatedRatgeber.title)} icon={<FileIcon/>} text={"PDF herunterladen"}></ButtonPrime>}
                    title={ /* ... dialog title (use direct imageUrl here too) ... */
                        <div className=''>
                            {post.relatedRatgeber.imageUrl &&
                                <img className='' src={post.relatedRatgeber.imageUrl} alt="" />
                            }
                           <div>
                               <span className="text-xs text-prime-600 font-medium block">Ratgeber Download</span>
                               <span className="text-lg font-semibold text-prime-900 block">{post.relatedRatgeber.title}</span>
                           </div>
                        </div>
                     }
                  >
                    {/* Your ContactForm - Keep this logic */}
                    <ContactForm
                        ratgeberName={post.relatedRatgeber.title}
                        fields={[ "name", "email", "phone", "agbAccepted"]}
                        redirect_url={post.relatedRatgeber.fileUrl} // Use the direct file URL
                        formDescription="Bitte geben Sie Ihre Daten ein, um den Download-Link für den Ratgeber zu erhalten."
                        button="Ratgeber herunterladen"
                        isRatgeberForm={true}
                        className="p-1"
                        merkmal={post.merkmal} // Pass the merkmal prop if needed
                        event={() => trackRatgeberAbgeschlossen(post.relatedRatgeber.fileUrl, post.relatedRatgeber.title)}
                        toastText="Ratgeber"
                    />
                  </DialogModal>
                </div>
              </div>
            </aside>
          )}

          {/* Related Posts */}
          {post.relatedPosts && post.relatedPosts.length > 0 && (
            <nav className="mt-12 md:mt-16 border-t border-prime-200 pt-8">
              <h3 className="text-xl md:text-2xl font-semibold text-prime-900 mb-5">Weitere interessante Beiträge</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {post.relatedPosts.map((related) => (
                  related?.slug?.current && ( // Ensure related post and slug exist
                     <RouterLink
                       key={related._id}
                       onClick={() => trackRelatedRatgeberClick(related.slug.current)}
                       to={`/ratgeber/${related.slug.current}`}
                       className="block border border-gray-200 p-4 rounded-lg hover:shadow-md hover:border-prime-300 transition group bg-white"
                     >
                        {/* Check for direct Related Post Image URL */}
                        {related?.mainImage?.asset.url && (
                            <img
                                src={related.mainImage.asset.url} // USE DIRECT URL
                                alt={related.mainImage.alt || related.title || ''}
                                className="w-full h-32 object-cover rounded-md mb-3"
                            />
                        )}
                        {!related?.mainImage.asset.url && ( // Optional placeholder
                           <div className="w-full h-32 bg-gray-200 rounded-md mb-3 flex items-center justify-center">
                               <span className="text-gray-500 text-sm">Kein Bild</span>
                           </div>
                        )}
                       <h4 className="font-semibold text-prime-800 group-hover:text-prime-600 mb-1 line-clamp-2">{related.title}</h4>
                       <span className="text-sm text-prime-600 group-hover:underline mt-2 inline-block">Mehr lesen →</span>
                     </RouterLink>
                  )
                ))}
              </div>
            </nav>
          )}
        </article>

        <Footer/>
    </div>
  );
}